import React, { memo } from 'react';
import { ScaleLinear } from 'd3';
import { brushHandleGripLinesPath, brushHandlePath, Arrow } from '../svg';
import { brushStyles } from './BrushStyles';

type BrushHandlesProps = {
  visibility: {
    flipLeftHandle: boolean;
    flipRightHandle: boolean;
    showLeftArrow: boolean;
    showRightArrow: boolean;
    leftHandleInView: boolean;
    rightHandleInView: boolean;
  };
  localBrushExtent: [number, number];
  xScale: ScaleLinear<number, number>;
  innerHeight: number;
  innerWidth: number;
  showLabels: boolean;
  hovering: boolean;
  brushLabelValue: (d: 'left' | 'right', x: number) => string;
  leftHandleColor: string;
  rightHandleColor: string;
};

type BrushHandleProps = {
  position: 'left' | 'right';
  color: string;
  value: number;
  xScale: ScaleLinear<number, number>;
  innerHeight: number;
  showLabel: boolean;
  flip: boolean;
  brushLabelValue: (d: 'left' | 'right', x: number) => string;
};

const BrushHandle = memo(function BrushHandle({
  position,
  color,
  value,
  xScale,
  innerHeight,
  showLabel,
  flip,
  brushLabelValue,
}: BrushHandleProps) {
  const isLeft = position === 'left';
  const x = Math.max(0, xScale(value));

  return (
    <g
      data-testid={`brush-handle-${position}`}
      transform={`translate(${x}, 0), scale(${flip ? '-1' : '1'}, 1)`}
      style={{ pointerEvents: 'none' }}
    >
      <g>
        {/* Handle background - blue rounded rectangle */}
        <path
          style={{
            ...brushStyles.handle,
            stroke: 'none',
            fill: color,
          }}
          d={brushHandlePath(innerHeight)}
        />
        {/* Pause icon - white vertical bars */}
        <path
          className="brush-handle-accent"
          style={{
            stroke: 'white',
            strokeWidth: 2,
            fill: 'none',
          }}
          d={brushHandleGripLinesPath(innerHeight)}
        />
      </g>

      <g
        style={{
          ...brushStyles.labelGroup,
          opacity: showLabel ? 1 : 0,
        }}
        transform={`translate(70,0), scale(${flip ? '1' : '-1'}, 1)`}
      >
        <rect style={brushStyles.tooltipBackground} y="0" x="-30" height="30" width="60" rx="8" />
        <text style={brushStyles.tooltip} transform="scale(-1, 1)" y="15" dominantBaseline="middle">
          {brushLabelValue(isLeft ? 'left' : 'right', value)}
        </text>
      </g>
    </g>
  );
});

export const BrushHandles = memo(function BrushHandles({
  visibility,
  localBrushExtent,
  xScale,
  innerHeight,
  innerWidth,
  showLabels,
  hovering,
  brushLabelValue,
  leftHandleColor,
  rightHandleColor,
}: BrushHandlesProps) {
  const handleYPosition = innerHeight / 2 - 7.5;

  return (
    <>
      {visibility.leftHandleInView && (
        <BrushHandle
          position="left"
          color={leftHandleColor}
          value={localBrushExtent[0]}
          xScale={xScale}
          innerHeight={innerHeight}
          showLabel={showLabels || hovering}
          flip={visibility.flipLeftHandle}
          brushLabelValue={brushLabelValue}
        />
      )}

      {visibility.rightHandleInView && (
        <BrushHandle
          position="right"
          color={rightHandleColor}
          value={localBrushExtent[1]}
          xScale={xScale}
          innerHeight={innerHeight}
          showLabel={showLabels || hovering}
          flip={visibility.flipRightHandle}
          brushLabelValue={brushLabelValue}
        />
      )}

      {visibility.showLeftArrow && (
        <g transform={`translate(0, ${handleYPosition})`}>
          <Arrow color={leftHandleColor} dataTestId="off-screen-handle-left" />
        </g>
      )}

      {visibility.showRightArrow && (
        <g transform={`translate(${innerWidth}, ${handleYPosition}) scale(-1, 1)`}>
          <Arrow color={rightHandleColor} dataTestId="off-screen-handle-right" />
        </g>
      )}
    </>
  );
});
